import React, { memo, useEffect } from 'react';
import styled from 'styled-components/macro';
import Select from 'react-select';
import { Box2Chart } from '../Box2Chart/Loadable';
import graphline from './graphline.svg';
import graphcard from './graphcard.svg';
import graphbar from './graphbar.svg';
import graphbarblue from './graphbarblue.svg';
import graphcardblue from './graphcardBlue.svg';
import graphlineblue from './graphlineblue.svg';
import { NetworthGrowthBar } from '../NetworthGrowthBar/Loadable';
import { useNetworthGrowthSlice } from '../Box2Chart/slice';
import { useDispatch, useSelector } from 'react-redux';
import { selectNetworthGrowth } from '../Box2Chart/slice/selectors';
import { Loader } from '../Loader/Loadable';

interface Props {
  networth: number;
}

type ChartData = {
  min: number;
  max: number;
  dataPoints: { date: string; value: number }[];
};

function transformNetWorth(response: any): ChartData {
  const filteredData = response.data.filter(
    item => item.total_value !== undefined,
  ) as {
    computed_at: string;
    total_value: number;
  }[];

  if (filteredData.length === 0) {
    return { min: 0, max: 0, dataPoints: [] };
  }

  const values = filteredData.map(item => item.total_value);
  const min = Math.min(...values);
  const max = Math.max(...values);

  const dataPoints = filteredData.map(item => ({
    date: item.computed_at.split('T')[0],
    value: item.total_value,
  }));

  return { min, max, dataPoints };
}

export const NetworthGrowthGraph = memo((props: Props) => {
  const [graphType, setGraphType] = React.useState('line');
  const [selectedTime, setSelectedTime] = React.useState('1M');
  const [Minvalue, setMinValue] = React.useState(0);
  const [MaxValue, setMaxValue] = React.useState(0);
  const [Datapoints, setDataPoints] = React.useState<
    { computed_at: string; total_value: number }[]
  >([]);

  const { actions } = useNetworthGrowthSlice();
  const { data, isLoading } = useSelector(selectNetworthGrowth);
  const dispatch = useDispatch();
  const [selectedNetworth, setSelectedNetworth] = React.useState(
    'Net worth performance',
  );

  const handleGraphTypeChange = (type: string) => {
    setGraphType(type);
  };

  useEffect(() => {
    if (!data) {
      dispatch(
        actions.networthGrowth({
          from: '2025-02-12T00:00:00Z',
          to: '2025-02-14T00:00:00Z',
        }),
      );
    }
  }, [data, actions, dispatch]);

  useEffect(() => {
    if (data) {
      const charData = transformNetWorth(data);
      setDataPoints(
        charData.dataPoints.map(({ date, value }) => ({
          computed_at: date,
          total_value: value,
        })),
      );
      setMinValue(charData.min);
      setMaxValue(charData.max);
    }
  }, [data]);

  const timeOptions = [
    { value: '1M', label: '1 Month' },
    { value: '3M', label: '3 Months' },
    { value: '3Y', label: '3 Years' },
    { value: '5Y', label: '5 Years' },
  ];

  return (
    <>
      {isLoading ? (
        <Loader />
      ) : (
        <Div className="h-100 w-100">
          <div className="p-2 d-flex justify-content-between align-items-center">
            <GraphType className="d-flex px-2 justify-content-between align-items-center">
              <GraphTypeImage
                src={graphType === 'line' ? graphlineblue : graphline}
                alt="graph type"
                onClick={() => handleGraphTypeChange('line')}
              />
              <GraphTypeImage
                src={graphType === 'bar' ? graphbarblue : graphbar}
                alt="graph type"
                onClick={() => handleGraphTypeChange('bar')}
              />
              <GraphTypeImage
                src={graphType === 'card' ? graphcardblue : graphcard}
                alt="graph type"
                onClick={() => handleGraphTypeChange('card')}
              />
            </GraphType>
            <div className="d-flex gap-2 align-items-center">
              <NetworthDiv
                className="px-1"
                value={selectedNetworth}
                onChange={e => setSelectedNetworth(e.target.value)}
              >
                <option>Net worth performance</option>
              </NetworthDiv>
              <Select
                options={timeOptions}
                value={timeOptions.find(
                  option => option.value === selectedTime,
                )}
                onChange={selectedOption =>
                  setSelectedTime(selectedOption?.value || '1M')
                }
                styles={Selectstyles}
              />
            </div>
          </div>
          <div className="w-100 h-75 px-3">
            {graphType === 'line' && (
              <div className="w-100" style={{ height: '90%' }}>
                <Box2Chart data={Datapoints} min={Minvalue} max={MaxValue} />
              </div>
            )}
            {graphType === 'bar' && (
              <div className="w-100" style={{ height: '90%' }}>
                <NetworthGrowthBar
                  networth={props.networth}
                  calculation_type={selectedTime}
                />
              </div>
            )}
            <div className="d-flex justify-content-between ps-5">
              <DateText>sep</DateText>
              <DateText>oct</DateText>
            </div>
          </div>
        </Div>
      )}
    </>
  );
});

const Div = styled.div``;
const GraphTypeImage = styled.img`
  width: 22px;
  height: 22px;
  cursor: pointer;
`;
const GraphType = styled.div`
  width: 156px;
  height: 45px;
  border: 1px solid #e4e6ec;
  border-radius: 10px;
`;
const NetworthDiv = styled.select`
  width: 214px;
  height: 45px;
  border: 1px solid #e4e6ec;
  border-radius: 10px;
`;
const DateText = styled.span`
  font-weight: 700;
  font-size: 11px;
  font-family: 'Nunito';
`;

const Selectstyles = {
  control: (base, state) => ({
    ...base,
    paddingLeft: '0%',
    fontSize: '1rem',
    border: '1.2px solid #e8e5f9',
    outline: 'none',
    minWidth: '100%',
    boxShadow: 'none',
    borderRadius: '10px',
    backgroundColor: '#FFFFF',
    height: '46px',
    gap: '15px',
    alignItems: 'center',
    '&:focus': {
      borderColor: '#FFFFF',
    },
    '&:hover': {
      borderColor: '#FFFFF',
    },
  }),
  container: base => ({
    ...base,
    width: '100%',
    flex: 1,
  }),
  input: base => ({
    ...base,
    margin: 0,
    padding: 0,
    fontWeight: '400',
    fontSize: '14px',
    width: '100%',
  }),
  placeholder: base => ({
    ...base,
    fontSize: '14px',
    fontWeight: 300,
  }),
  singleValue: base => ({
    ...base,
    fontSize: '1rem',
    width: '100%',
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? '#E6E6FA'
      : state.isFocused
      ? '#F8F7FF'
      : 'white',
    color: '#333',
    padding: '8px 16px',
    borderBottom: '1px solid #E4DFFF',
    '&:active': {
      backgroundColor: '#E6E6FA',
    },
  }),
  menu: base => ({
    ...base,
    borderRadius: '16px',
    overflowY: 'auto',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    width: '90%',
  }),
  menuList: base => ({
    ...base,
    padding: '8px 0',
  }),
};
